import "./RegisterForm.style.css";

import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { ApiFactory } from "../../../api-client/api-factory";
import { RegisterRequest, UsersApi } from "../../../api-client";
import { validateEmail } from "../../../utils/functions/validation/validate-email";
import { validatePassword } from "../../../utils/functions/validation/validate-password";
import {
  Button,
  ButtonType,
  ButtonColor,
} from "../../../components/base/Button/Button";

interface RegisterFormData {
  username: string;
  email: string;
  password: string;
  repeatedPassword: string;
}

type ValidationErrors = {
  username: string | null;
  email: string | null;
  password: string | null;
  repeatedPassword: string | null;
};

type RegisterFormProps = {
  setIsLogin: (newIsLogin: boolean) => void;
};

export const RegisterForm = ({ setIsLogin }: RegisterFormProps) => {
  const [registerFormData, setRegisterFormData] = useState<RegisterFormData>({
    username: "",
    email: "",
    password: "",
    repeatedPassword: "",
  });

  const hints = {
    username: "Пример: yourdler",
    email: "Пример: yourdler@mail.ru",
    password:
      "Пароль должен содержать 8 символов, цифру, букву строчную, букву заглавную и спец. символ",
  };

  const [errors, setErrors] = useState<ValidationErrors>({
    username: null,
    email: null,
    password: null,
    repeatedPassword: null,
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  const calculateErrors = (data: RegisterFormData): ValidationErrors => {
    const newErrors: ValidationErrors = {
      username: null,
      email: null,
      password: null,
      repeatedPassword: null,
    };

    if (data.username.trim().length < 3) {
      newErrors.username =
        "Никнейм должен содержать минимум 3 символа (пример: yourdler)";
    }

    if (!validateEmail(data.email)) {
      newErrors.email =
        "Пожалуйста, введите корректный email-адрес (пример: yourdler@mail.ru)";
    }

    if (!validatePassword(data.password)) {
      newErrors.password =
        "Пароль не соответствует требованиям безопасности (длина 8, цифра, буква строчная, буква заглавная, спец. символ)";
    }

    if (data.password !== data.repeatedPassword) {
      newErrors.repeatedPassword = "Пароли не совпадают";
    }

    return newErrors;
  };

  useEffect(() => {
    if (formSubmitted) {
      const calculatedErrors = calculateErrors(registerFormData);
      setErrors(calculatedErrors);
    }
  }, [registerFormData, formSubmitted]);

  const validateForm = (): boolean => {
    const noErrors =
      !errors.username &&
      !errors.email &&
      !errors.password &&
      !errors.repeatedPassword;
    const allFieldsFilled =
      registerFormData.username.trim().length > 0 &&
      registerFormData.email.length > 0 &&
      registerFormData.password.length > 0 &&
      registerFormData.repeatedPassword.length > 0 &&
      registerFormData.password === registerFormData.repeatedPassword;
    return noErrors && allFieldsFilled;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (formSubmitted) {
      validateForm();
    }
  };

  const handleSubmitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormSubmitted(true);

    const currentErrors = calculateErrors(registerFormData);
    setErrors(currentErrors);

    if (
      Object.values(currentErrors).every((err) => err === null) &&
      registerFormData.username.trim().length > 0 &&
      registerFormData.email.length > 0 &&
      registerFormData.password.length > 0 &&
      registerFormData.repeatedPassword.length > 0
    ) {
      const request: RegisterRequest = {
        username: registerFormData.username,
        email: registerFormData.email,
        password: registerFormData.password,
      };

      ApiFactory.get(UsersApi)
        .registerPost(request)
        .then(() => {
          console.log("Вы зарегистрированы!");
          setIsLogin(true);
        })
        .catch(() =>
          console.error(
            "Не получилось зарегистрировать. Возможно, этот ник уже занят, либо вы не ввели корректные данные",
          ),
        );
    } else {
      console.error("В форме есть ошибки");
    }
  };

  return (
    <form onSubmit={handleSubmitForm} className="register-form">
      <div className="register-form__blocks">
        <div className="register-form__block">
          <div className="register-form__input header_4">
            <label htmlFor="username">Никнейм</label>
            <input
              type="text"
              id="username"
              name="username"
              onChange={handleChange}
              className={`register-form__email_input text_2 ${errors.username ? "error" : ""}`}
              value={registerFormData.username}
              aria-invalid={!!errors.username}
              aria-describedby={
                formSubmitted && errors.password
                  ? "username-error"
                  : "username-hints"
              }
            />
          </div>
          {formSubmitted && errors.username ? (
            <p id="username-error" className="error-message text_2">
              {errors.username}
            </p>
          ) : (
            <p id="username-hints" className="hints-message text_2">
              {hints.username}
            </p>
          )}
        </div>
        <div className="register-form__block">
          <div className="register-form__input header_4">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              onChange={handleChange}
              className={`register-form__email_input text_2 ${errors.email ? "error" : ""}`}
              value={registerFormData.email}
              aria-invalid={!!errors.email}
              aria-describedby={
                formSubmitted && errors.password ? "email-error" : "email-hints"
              }
            />
          </div>
          {formSubmitted && errors.email ? (
            <p id="email-error" className="error-message text_2">
              {errors.email}
            </p>
          ) : (
            <p id="email-hints" className="hints-message text_2">
              {hints.email}
            </p>
          )}
        </div>
        <div className="register-form__block">
          <div className="register-form__input header_4">
            <label htmlFor="password">Пароль</label>
            <input
              type="password"
              id="password"
              name="password"
              onChange={handleChange}
              className={`register-form__password_input text_2 ${errors.password ? "error" : ""}`}
              value={registerFormData.password}
              aria-invalid={!!errors.password}
              aria-describedby={
                formSubmitted && errors.password
                  ? "password-error"
                  : "password-hints"
              }
            />
          </div>
          {formSubmitted && errors.password ? (
            <p id="password-error" className="error-message text_2">
              {errors.password}
            </p>
          ) : (
            <p id="password-hints" className="hints-message text_2">
              {hints.password}
            </p>
          )}
        </div>
        <div className="register-form__block">
          <div className="register-form__input header_4">
            <label htmlFor="repeatedPassword">Пароль еще раз</label>
            <input
              type="password"
              id="repeatedPassword"
              name="repeatedPassword"
              onChange={handleChange}
              className={`register-form__repeated_password_input text_2 ${errors.repeatedPassword ? "error" : ""} ${registerFormData.password === registerFormData.repeatedPassword && registerFormData.repeatedPassword.length > 0 ? "password-match" : ""}`}
              value={registerFormData.repeatedPassword}
              aria-invalid={!!errors.repeatedPassword}
              aria-describedby="repeatedPassword-error"
            />
          </div>
          {formSubmitted && errors.repeatedPassword && (
            <p id="repeatedPassword-error" className="error-message text_2">
              {errors.repeatedPassword}
            </p>
          )}
        </div>
      </div>
      <Button
        text="Зарегистрироваться"
        type={ButtonType.Submit}
        color={ButtonColor.Secondary}
        disabled={!validateForm()}
      />
    </form>
  );
};
